import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import { Card, Typography, CircularProgress, Grid, Switch, Fade, Box } from "@mui/material";
import { useState, useEffect, useRef } from "react";

import { priceService } from "services/priceService";
import MKInput from "components/MKInput";
import PropTypes from "prop-types";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

function SubscriptionModificationForm({ productKey, title, description }) {
  const [loading, setLoading] = useState(false);
  const [currentValue, setCurrentValue] = useState("");
  const [newValue, setNewValue] = useState("");
  const [checked, setChecked] = useState(false);
  const [checkboxDisabled, setCheckboxDisabled] = useState(false);

  const [alert, setAlert] = useState(null);
  const timerRef = useRef();

  const showAlert = (severity, message) => {
    setAlert({ severity, message });
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setAlert(null);
    }, 6000);
  };

  /* ---------- handlers ---------- */

  const fetchSetting = async () => {
    setLoading(true);
    try {
      const setting = await priceService.getCreditPrice(productKey);
      setCurrentValue(setting.credits);
      setNewValue(setting.credits);
    } catch (error) {
      console.error("Error fetching setting:", error);
      showAlert("error", "Error fetching setting");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, [productKey]);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    if (currentValue === newValue) {
      setCheckboxDisabled(true);
    } else {
      setCheckboxDisabled(false);
    }
  }, [currentValue, newValue]);

  const handleChange = (value) => {
    setChecked(false);
    setNewValue(value);
  };

  const handleChecked = (e) => {
    setChecked(e.target.checked);
  };

  const handleCancel = () => {
    setNewValue(currentValue);
    setChecked(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !confirm(
        `Weet je zeker dat je de instelling "${title}" wilt wijzigen van ${currentValue} naar ${newValue}?`
      )
    ) {
      return;
    }

    setLoading(true);
    priceService
      .updateCreditsForPrice(productKey, newValue)
      .then(() => {
        fetchSetting();
        showAlert("success", "Instelling opgeslagen");
        setChecked(false);
      })
      .catch((error) => {
        console.error("Error updating setting:", error);
        showAlert("error", "Error updating setting");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return (
      <MKBox display="flex" justifyContent="center" alignItems="center" height="400px" width="100%">
        <CircularProgress size={40} />
      </MKBox>
    );
  }

  return (
    <MKBox paddingBottom={2}>
      <Card sx={{ width: "100%", mb: 4 }}>
        <MKBox component="form" autoComplete="off" onSubmit={handleSubmit} p={3} width="100%">
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {description}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={1}>
              <MKTypography variant="body2" sx={{ mb: 2, textAlign: "right" }}>
                {" "}
              </MKTypography>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MKInput
                type="number"
                inputProps={{
                  step: "1",
                  min: "0",
                  max: "999999",
                }}
                value={newValue}
                onChange={(e) => handleChange(e.target.value)}
                variant="standard"
                placeholder="Waarde"
                fullWidth
                sx={{
                  "& .MuiInput-root": {
                    fontSize: "15px",
                  },
                  "& input": {
                    textAlign: "center",
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} alignItems="center" ml={-1} mt={2}>
            <Switch checked={checked} onChange={handleChecked} disabled={checkboxDisabled} />
            {checkboxDisabled ? (
              <MKTypography variant="button" color="light">
                Deze instelling is al ingesteld op deze waarde.
              </MKTypography>
            ) : (
              <MKTypography variant="button">
                Ik weet zeker dat ik de instelling <strong>{title}</strong> wil wijzigen van{" "}
                <strong>{currentValue}</strong> naar <strong>{newValue}</strong>
              </MKTypography>
            )}
          </Grid>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              {alert && (
                <Fade in={!!alert}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {alert.severity === "error" ? (
                      <ErrorOutlineIcon color="error" fontSize="small" />
                    ) : (
                      <CheckCircleOutlineIcon color="success" fontSize="small" />
                    )}
                    <MKTypography
                      variant="body2"
                      color={alert.severity === "error" ? "error" : "success"}
                    >
                      {alert.message}
                    </MKTypography>
                  </Box>
                </Fade>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2} justifyContent="flex-end">
                <Grid item>
                  <MKButton variant="outlined" color="dark" onClick={handleCancel} fullWidth>
                    Annuleren
                  </MKButton>
                </Grid>
                <Grid item>
                  <MKButton
                    variant="contained"
                    color="dark"
                    type="submit"
                    disabled={!checked || newValue === currentValue}
                  >
                    Opslaan
                  </MKButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </MKBox>
      </Card>
    </MKBox>
  );
}

export default SubscriptionModificationForm;

SubscriptionModificationForm.propTypes = {
  productKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};
